// ============================================================================
// PRELIT-RECEIPTS-001 — Branch Verification of Recorded Receipts
// ============================================================================
// Optional branch worksheet (Resources/info 2.txt §3). Lists honoured/partial
// commitments captured by field agents. Branch users mark each receipt as
// VERIFIED or DISPUTED. Case remains untouched — this is a bookkeeping
// reconciliation, not a workflow trigger.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import { useReceipts } from 'hooks/api/usePreLitigation';
import type {
  ReceiptRow,
  ReceiptsSearchRequest,
} from 'api/types/preLitigation.types';
import { openCaseDetail } from 'components/modules/pre-litigation-module/openCaseDetail';
import VerifyReceiptModal from './VerifyReceiptModal';

interface Props {
  tabId?: string;
  moduleName?: string;
}

type VerificationTab = 'PENDING' | 'VERIFIED' | 'DISPUTED' | 'ALL';

const VERIFICATION_TABS: { id: VerificationTab; label: string; icon: keyof typeof IconSystem }[] = [
  { id: 'PENDING',  label: 'Pending Verification', icon: 'bell' },
  { id: 'VERIFIED', label: 'Verified',             icon: 'checkCircle' },
  { id: 'DISPUTED', label: 'Disputed',             icon: 'xCircle' },
  { id: 'ALL',      label: 'All',                  icon: 'list' },
];

const HONOURED_STATUS_OPTIONS = [
  { value: 'ALL',      label: 'All' },
  { value: 'HONOURED', label: 'Fully Honoured' },
  { value: 'PARTIAL',  label: 'Partial' },
];

const formatDate = (v?: string) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return v;
  }
};

const ReceiptsScreen: React.FC<Props> = ({ tabId }) => {
  const [activeTab, setActiveTab] = useState<VerificationTab>('PENDING');
  const [queryParams, setQueryParams] = useState<ReceiptsSearchRequest>({
    page: 1,
    pageSize: 25,
    verificationStatus: 'PENDING',
    honouredStatus: 'ALL',
  });
  const { data, loading, pagination, refetch } = useReceipts(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter panel state
  const [honouredStatusFilter, setHonouredStatusFilter] = useState('ALL');
  const [receivedFrom, setReceivedFrom]                 = useState('');
  const [receivedTo, setReceivedTo]                     = useState('');

  // Modals
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [selectedRow, setSelectedRow]         = useState<ReceiptRow | null>(null);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleTabChange = useCallback((tab: VerificationTab) => {
    setActiveTab(tab);
    setQueryParams(prev => ({ ...prev, verificationStatus: tab, page: 1 }));
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      honouredStatus: honouredStatusFilter || 'ALL',
      receivedFrom:   receivedFrom   || undefined,
      receivedTo:     receivedTo     || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [honouredStatusFilter, receivedFrom, receivedTo]);

  const clearFilters = useCallback(() => {
    setHonouredStatusFilter('ALL'); setReceivedFrom(''); setReceivedTo('');
    setQueryParams(prev => ({
      page: 1,
      pageSize: prev.pageSize ?? 25,
      verificationStatus: prev.verificationStatus,
      honouredStatus: 'ALL',
    }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.honouredStatus && queryParams.honouredStatus !== 'ALL') n++;
    if (queryParams.receivedFrom) n++;
    if (queryParams.receivedTo)   n++;
    return n;
  }, [queryParams]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // Grid columns
  const columns: SyutGridColumn<ReceiptRow>[] = useMemo(() => [
    { id: 'caseCode',      header: 'Case #',   accessorKey: 'caseCode',      size: 120, enableSorting: true },
    { id: 'accountNumber', header: 'Account #', accessorKey: 'accountNumber', size: 130, enableSorting: true },
    { id: 'customerName',  header: 'Customer', accessorKey: 'customerName',  size: 180, enableSorting: true, flex: true },
    {
      id: 'branchCode', header: 'Branch',
      accessorKey: 'branchCode', size: 90,
      cell: ({ row }) => row.original.branchCode ?? '—',
    },
    {
      id: 'committedAmount', header: 'Committed ₹',
      accessorKey: 'committedAmount', size: 120, enableSorting: true,
      cell: ({ row }) => `₹${(row.original.committedAmount ?? 0).toLocaleString('en-IN')}`,
    },
    {
      id: 'committedDate', header: 'Committed Date',
      accessorKey: 'committedDate', size: 130, enableSorting: true,
      cell: ({ row }) => formatDate(row.original.committedDate),
    },
    {
      id: 'honouredAmount', header: 'Received ₹',
      accessorKey: 'honouredAmount', size: 120, enableSorting: true,
      cell: ({ row }) => (
        <span style={{ color: '#16a34a', fontWeight: 600 }}>
          ₹{(row.original.honouredAmount ?? 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      id: 'honouredStatus', header: 'Type',
      accessorKey: 'honouredStatus', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const s = row.original.honouredStatus;
        const color = s === 'HONOURED' ? '#16a34a' : '#d97706';
        const label = s === 'HONOURED' ? 'Full' : 'Partial';
        return (
          <span style={{
            fontSize: '0.72rem', fontWeight: 600,
            padding: '2px 8px', borderRadius: '999px',
            color, border: `1px solid ${color}`,
          }}>{label}</span>
        );
      },
    },
    {
      id: 'honouredAt', header: 'Received At',
      accessorKey: 'honouredAt', size: 130, enableSorting: true,
      cell: ({ row }) => formatDate(row.original.honouredAt),
    },
    {
      id: 'capturedByName', header: 'Captured By',
      accessorKey: 'capturedByName', size: 150,
      cell: ({ row }) => row.original.capturedByName ?? '—',
    },
    {
      id: 'receiptVerificationStatus', header: 'Verification',
      accessorKey: 'receiptVerificationStatus', size: 120, enableSorting: true,
      cell: ({ row }) => {
        const s = row.original.receiptVerificationStatus;
        const color = s === 'VERIFIED' ? '#16a34a' : s === 'DISPUTED' ? '#dc2626' : '#d97706';
        const label = s === 'VERIFIED' ? 'Verified' : s === 'DISPUTED' ? 'Disputed' : 'Pending';
        return (
          <span style={{
            fontSize: '0.72rem', fontWeight: 600,
            padding: '2px 8px', borderRadius: '999px',
            color, border: `1px solid ${color}`,
          }}>{label}</span>
        );
      },
    },
    {
      id: 'receiptVerifiedByName', header: 'Verified By',
      accessorKey: 'receiptVerifiedByName', size: 150,
      cell: ({ row }) => row.original.receiptVerifiedByName ?? '—',
    },
  ], []);

  // Row actions
  const getRowActions = useCallback((row: ReceiptRow): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'open',
        label: 'Open Case',
        icon: <IconSystem.eye size={16} />,
        onClick: () => openCaseDetail(String(row.caseId)),
      },
    ];
    if (row.receiptVerificationStatus === 'PENDING') {
      items.push({
        id: 'verify',
        label: 'Verify Receipt',
        icon: <IconSystem.checkCircle size={16} />,
        onClick: () => { setSelectedRow(row); setVerifyModalOpen(true); },
      });
    } else {
      items.push({
        id: 're-verify',
        label: 'Update Verdict',
        icon: <IconSystem.edit size={16} />,
        onClick: () => { setSelectedRow(row); setVerifyModalOpen(true); },
      });
    }
    return items;
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Reload receipts',
      onClick: refetch,
      variant: 'icon',
    },
  ], [refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Receipt Verification"
        description="Branch worksheet — verify or dispute honoured commitments recorded by field agents."
        icon={<IconSystem.dollarSign size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search account, customer, case #...',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      {/* Status tabs — directly below ModuleHeader */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        padding: '0.5rem 1.25rem 0',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-primary)',
        flexShrink: 0,
        overflowX: 'auto',
      }}>
        {VERIFICATION_TABS.map((tab) => {
          const Icon = (IconSystem as any)[tab.icon] ?? IconSystem.list;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.9rem',
                border: 'none',
                background: 'transparent',
                color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: '0.85rem',
                fontWeight: isActive ? 600 : 500,
                borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              frozenRightColumns={['actions']}
              emptyMessage={loading ? 'Loading...' : 'No receipts in this tab.'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Honoured Type"
                  options={HONOURED_STATUS_OPTIONS}
                  value={honouredStatusFilter}
                  onChange={(val) => setHonouredStatusFilter((val as string) || 'ALL')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Received From</label>
                <input type="date" className="form-input" value={receivedFrom} onChange={(e) => setReceivedFrom(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Received To</label>
                <input type="date" className="form-input" value={receivedTo} onChange={(e) => setReceivedTo(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <VerifyReceiptModal
        isOpen={verifyModalOpen}
        row={selectedRow}
        onClose={() => setVerifyModalOpen(false)}
        onSuccess={refetch}
      />
    </div>
  );
};

export default ReceiptsScreen;
